import type { DockviewApi, DockviewGroupPanel } from 'dockview-react';

/** The windows of the Toolbox (docked along the bottom, tabs underneath), in tab order. */
export const TOOLBOX: { id: string; title: string }[] = [
  { id: 'trade', title: 'Trade' },
  { id: 'exposure', title: 'Exposure' },
  { id: 'history', title: 'History' },
  { id: 'mailbox', title: 'Mailbox' },
  { id: 'alerts', title: 'Alerts' },
  { id: 'challenge', title: 'Challenge' },
  { id: 'notifications', title: 'Journal' },
];
const TOOLBOX_IDS = new Set(TOOLBOX.map((t) => t.id));
/** Windows that draw their own title bar (and their own tabs underneath), so the dock's tab strip is hidden for them. */
const TITLED = new Set(['marketWatch', 'navigator', 'dataWindow']);
/** Windows whose tabs go underneath, as in the classic terminal. */
const TABS_BELOW = new Set(['chart', 'ticks', ...TOOLBOX_IDS]);

export const isToolboxPanel = (component: string) => TOOLBOX_IDS.has(component);

/**
 * The classic terminal's arrangement: Market Watch above the Navigator on the left, the charts filling the rest, and the
 * Toolbox (Trade, Exposure, History, …) across the whole bottom. The order ticket opens as a window of its own (F9).
 */
export function buildClassicLayout(api: DockviewApi, challenge: boolean): void {
  api.clear();
  const marketWatch = api.addPanel({ id: 'marketWatch', component: 'marketWatch', title: 'Market Watch' });
  const chart = api.addPanel({
    id: 'chart',
    component: 'chart',
    title: 'Chart',
    position: { referencePanel: marketWatch, direction: 'right' },
  });
  const navigator = api.addPanel({
    id: 'navigator',
    component: 'navigator',
    title: 'Navigator',
    position: { referencePanel: marketWatch, direction: 'below' },
  });
  const trade = api.addPanel({
    id: 'trade',
    component: 'trade',
    title: 'Trade',
    position: { direction: 'below' },
  });
  for (const t of TOOLBOX.slice(1)) {
    if (t.id === 'challenge' && !challenge) continue;
    api.addPanel({
      id: t.id,
      component: t.id,
      title: t.title,
      position: { referencePanel: trade, direction: 'within' },
    });
  }
  marketWatch.api.setSize({ width: 300 });
  trade.api.setSize({ height: 210 });
  navigator.api.setSize({ height: 220 });
  trade.api.setActive();
  chart.api.setActive();
  styleGroups(api);
}

/** Where a closed window reopens in the classic workspace (the order ticket floats, like the terminal's order dialog). */
export function addClassicPanel(api: DockviewApi, id: string, title: string): boolean {
  if (id === 'order') {
    const box = api.width > 0 ? api : { width: 1200, height: 800 };
    api.addPanel({
      id,
      component: id,
      title,
      floating: { width: 380, height: 560, x: Math.max(20, box.width / 2 - 190), y: 40 },
    });
    return true;
  }
  if (id === 'navigator') {
    const near = api.getPanel('marketWatch') ?? api.getPanel('dataWindow');
    api.addPanel({
      id,
      component: id,
      title,
      position: near ? { referencePanel: near, direction: 'below' } : { direction: 'left' },
    });
    return true;
  }
  if (id === 'marketWatch') {
    const near = api.getPanel('navigator') ?? api.getPanel('dataWindow');
    api.addPanel({
      id,
      component: id,
      title,
      position: near ? { referencePanel: near, direction: 'above' } : { direction: 'left' },
    });
    return true;
  }
  if (TOOLBOX_IDS.has(id)) {
    const open = api.panels.find((p) => TOOLBOX_IDS.has(p.api.component) && p.api.location.type === 'grid');
    api.addPanel({
      id,
      component: id,
      title,
      position: open ? { referencePanel: open, direction: 'within' } : { direction: 'below' },
    });
    if (!open) api.getPanel(id)?.api.setSize({ height: 210 });
    return true;
  }
  return false;
}

/** Shows or hides the whole Toolbox (Ctrl+T in the classic terminal); returns whether it is now shown. */
export function toggleToolbox(api: DockviewApi, challenge: boolean): boolean {
  const open = api.panels.filter((p) => TOOLBOX_IDS.has(p.api.component));
  if (open.length > 0) {
    for (const p of open) p.api.close();
    return false;
  }
  for (const t of TOOLBOX) {
    if (t.id === 'challenge' && !challenge) continue;
    addClassicPanel(api, t.id, t.title);
  }
  api.getPanel('trade')?.api.setActive();
  return true;
}

/**
 * Tabs underneath for charts and the Toolbox, no tab strip for windows with a title bar of their own (unless something was
 * docked with them, which then needs the tabs). Called after every layout change; it changes a group only when needed.
 */
export function styleGroups(api: DockviewApi): void {
  for (const group of api.groups) styleGroup(group);
}

function styleGroup(group: DockviewGroupPanel): void {
  const components = group.panels.map((p) => p.api.component);
  const floating = group.api.location.type === 'floating';
  const hide = !floating && components.length === 1 && TITLED.has(components[0]!);
  if (group.model.header.hidden !== hide) group.model.header.hidden = hide;
  const below = !floating && components.some((c) => TABS_BELOW.has(c));
  const position = below ? 'bottom' : 'top';
  if (group.api.getHeaderPosition() !== position) group.api.setHeaderPosition(position);
}

/** Resets how the groups look when leaving the classic workspace. */
export function unstyleGroups(api: DockviewApi): void {
  for (const group of api.groups) {
    if (group.model.header.hidden) group.model.header.hidden = false;
    if (group.api.getHeaderPosition() !== 'top') group.api.setHeaderPosition('top');
  }
}
